import styled from 'styled-components';
import React, { useEffect, useState } from "react";
import { Dropdown, Menu, Space, TabsProps } from 'antd';
import { Badge, Button, Card, Form, PageHeader, Table, Tabs, InputNumber, Input, Modal, message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  HomeOutlined,
  UserOutlined,
  CalendarOutlined,
  BankOutlined,
  DownOutlined,
  EditOutlined
} from "@ant-design/icons";
import { ancanaApi } from "../../api/ancanaApi";
import moment from "moment";
import useQuery from "../../hooks/useQuery";
import { financeAmtFormat } from "../../utils";
import { ExpenseStatementModel, IExpenseStatement, IOwnerStatement } from "../../types/models/statements";
import RecordTable from "../../components/RecordTable";
import { InvoiceModel } from "../../types/models";
import CustomActionButton from "../../components/CustomActionButton";
import { useTheme } from "../../contexts/ThemeProvider";

const Container = styled.div<{ isDarkTheme: boolean }>`
  overflow-y: scroll;

  .ant-descriptions-item {
    padding-bottom: 0 !important;
  }

  .stat-card {
    width: 240px;
    text-align: center;
  }

  .editable-cell-value-wrap {
    padding-top: 0 !important;
    padding-bottom: 0 !important;

    :hover {
      padding-top: 1px;
      padding-bottom: 1px;
      border: 1px solid ${props => props.isDarkTheme ? '#424242' : '#d9d9d9'};
      border-radius: 2px;
    }
  }

  .ant-tabs-tab {
    letter-spacing: -0.5px;
    font-size: 13px;

    .anticon {
      margin-right: 8px;
    }
  }

  .sticky-bar {
    top: 0;
    position: sticky;
    z-index: 5;
    background-color: ${props => props.isDarkTheme ? '#1e1e1e' : '#f0f2f5'};
    height: 32px;
  }

  .numbers-cont {
    text-align: right;
  }

  .ant-table-cell {
    padding: 6px 12px !important;
  }

  .ant-table-summary {
    background: ${props => props.isDarkTheme ? '#333333' : 'rgb(251, 251, 250)'};
  }

  .balance-summary {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
  }

  .balance-card {
    background: ${props => props.isDarkTheme ? '#333333' : '#fafafa'};
    border: 1px solid ${props => props.isDarkTheme ? '#424242' : '#e8e8e8'};
    border-radius: 4px;
    padding: 0 12px;
    min-width: 240px;
    text-align: right;
  }

  .balance-row {
    display: flex;
    justify-content: space-between;
    margin: 8px 0;
  }

  .balance-label {
    font-weight: 500;
    color: ${props => props.isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : '#666'};
  }

  .balance-amount {
    font-weight: 600;
    // font-family: monospace;
  }

  .balance-amount.positive {
    color: ${props => props.isDarkTheme ? '#52c41a' : '#399809'};
  }

  .balance-amount.negative {
    color: #ff4d4f;
  }

  .property-header {
    background: ${props => props.isDarkTheme ? '#333333' : '#f8f8f8'};
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 24px;
  }

  .property-info {
    display: flex;
    gap: 24px;
    align-items: center;
  }

  .property-detail {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .property-label {
    font-size: 12px;
    color: ${props => props.isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : '#666'};
    font-weight: 500;
  }

  .property-value {
    font-size: 14px;
    font-weight: 600;
  }

  .header-row {
    background-color: ${props => props.isDarkTheme ? 'rgba(196, 150, 98, 0.15)' : 'rgba(230, 207, 165, 0.33)'};
  }
`;

const renderTabBar: TabsProps['renderTabBar'] = (props, DefaultTabBar) => {
  const DefaultTabBarComponent = DefaultTabBar as any;
  return <DefaultTabBarComponent {...props} className="sticky-bar sidento" />
};

// Editable cell component for expense amounts
const EditableCell: React.FC<{
  editing?: boolean;
  dataIndex?: string;
  title?: string;
  record?: any;
  index?: number;
  children?: React.ReactNode;
  onSave?: (record: any) => void;
  [key: string]: any;
}> = ({ editing = false, dataIndex = '', title = '', record = {}, children, onSave, ...restProps }) => {
  const [value, setValue] = useState<number>(0);
  const [form] = Form.useForm();

  useEffect(() => {
    if (editing && record && dataIndex) {
      const initialValue = record[dataIndex] || 0;
      setValue(initialValue);
      form.setFieldsValue({ [dataIndex]: initialValue });
    }
  }, [editing, record, dataIndex, form]);

  const handleSave = () => {
    if (onSave && record && dataIndex) {
      const updatedRecord = { ...record, [dataIndex]: value };
      onSave(updatedRecord);
    }
  };

  const handleChange = (newValue: number | null) => {
    setValue(newValue || 0);
  };

  return (
    <td {...restProps}>
      {editing ? (
        <Form form={form} component={false}>
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please input ${title}!`,
              },
            ]}
          >
            <InputNumber
              value={value}
              onChange={handleChange}
              onPressEnter={handleSave}
              onBlur={handleSave}
              style={{ width: '100%' }}
              formatter={(value) => `${value || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseFloat(value?.replace(/\$\s?|(,*)/g, '') || '0')}
            />
          </Form.Item>
        </Form>
      ) : (
        <div
          className="editable-cell-value-wrap"
          style={{ paddingRight: 24 }}
        >
          {children}
        </div>
      )}
    </td>
  );
};

function StatementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { query, handleFilter } = useQuery();
  const { currentTheme } = useTheme();
  const isDarkTheme = currentTheme === 'dark';
  const [statement, setStatement] = useState<IExpenseStatement>();
  const [ownerStatements, setOwnerStatements] = useState<IOwnerStatement[]>([]);
  const [currentTab, setCurrentTab] = useState<string>();
  const [editingKey, setEditingKey] = useState<string>('');
  const [expenseData, setExpenseData] = useState<any[]>([]);
  const [addExpenseModalVisible, setAddExpenseModalVisible] = useState<boolean>(false);
  const [selectedOwnerStatementId, setSelectedOwnerStatementId] = useState<number | null>(null);
  const [addExpenseForm] = Form.useForm();
  const [updatingStatement, setUpdatingStatement] = useState(false);


  const changeStatus = async (is_finalized: boolean) => {
    if (!!statement) {
        setUpdatingStatement(true);
        await ancanaApi.patch(`expense-statements/${id}/`, { is_finalized });
        setStatement({ ...statement, is_finalized });
        setUpdatingStatement(false);
    }
}
  const isEditing = (record: any) => record.key === editingKey;

  const edit = (record: any) => {
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (record: any) => {
    try {
      // Update local state
      const newData = [...expenseData];
      const index = newData.findIndex(item => record.key === item.key);
      if (index > -1) {
        newData[index] = record;
        setExpenseData(newData);

        // Update the statement's expenses_json and send to API
        if (statement) {
          const updatedExpensesJson = newData.map(item => ({
            code: item.code,
            description: item.description,
            total_amount: item.total_amount,
            sold_fractions_amount: item.sold_fractions_amount,
            billable: item.billable
          }));

          // Calculate new totals
          const newTotalExpenses = newData.reduce((sum, item) => sum + item.total_amount, 0);
          const newFinalBalance = statement.expense_initial_balance - newTotalExpenses;

          // Update statement locally
          setStatement({
            ...statement,
            expenses_json: updatedExpensesJson,
            total_expenses: newTotalExpenses,
            expense_final_balance: newFinalBalance
          });

          // Save to API
          await ancanaApi.patch(`expense-statements/${id}/`, {
            expenses_json: updatedExpensesJson,
            total_expenses: newTotalExpenses,
            expense_final_balance: newFinalBalance
          });
        }
      }
      setEditingKey('');
    } catch (errInfo) {
      console.error('Save Failed:', errInfo);
      // Optionally show error message to user
    }
  };

  const handleAddExpenseItem = (ownerStatementId: number) => {
    setSelectedOwnerStatementId(ownerStatementId);
    setAddExpenseModalVisible(true);
    addExpenseForm.resetFields();
  };

  const handleAddExpenseSubmit = async () => {
    try {
      const values = await addExpenseForm.validateFields();

      if (!selectedOwnerStatementId) return;

      // Find the current owner statement
      const currentOwnerStatement = ownerStatements.find(os => os.id === selectedOwnerStatementId);
      if (!currentOwnerStatement) return;

      // Create the new expense item
      const newExpenseItem = {
        description: values.description,
        property_total: parseFloat(values.property_total || '0'),
        fraction_amount: parseFloat(values.fraction_amount || '0'),
        running_balance: parseFloat(values.running_balance || '0')
      };

      // Update the expenses_json array
      const updatedExpensesJson = [...(currentOwnerStatement.expenses_json || []), newExpenseItem];

      // Update via API
      await ancanaApi.patch(`owner-statements/${selectedOwnerStatementId}/`, {
        expenses_json: updatedExpensesJson
      });

      // Update local state
      const updatedOwnerStatements = ownerStatements.map(os =>
        os.id === selectedOwnerStatementId
          ? { ...os, expenses_json: updatedExpensesJson }
          : os
      );
      setOwnerStatements(updatedOwnerStatements);

      // Close modal and show success message
      setAddExpenseModalVisible(false);
      message.success('Expense item added successfully');

    } catch (error) {
      console.error('Error adding expense item:', error);
      message.error('Failed to add expense item');
    }
  };

  useEffect(() => {
    // Fetch statement details
    ancanaApi.get(`expense-statements/${id}/`).then(res => {
      setStatement(res.data);

      // Process expenses data for the table
      const expensesJson = res.data.expenses_json || [];
      const processedExpenses = expensesJson.map((expense: any, index: number) => ({
        key: index.toString(),
        code: expense?.code || '',
        description: expense?.description || '',
        total_amount: expense?.total_amount || 0,
        sold_fractions_amount: expense?.sold_fractions_amount || 0,
        billable: expense?.billable || false
      }));
      setExpenseData(processedExpenses);
    }).catch(error => {
      console.error('Error fetching statement:', error);
    });

    // Fetch owner statements
    ancanaApi.get(`owner-statements/?expense_statement=${id}&page_size=50`).then(res => {
      setOwnerStatements(res.data.results || []);
    }).catch(error => {
      console.error('Error fetching owner statements:', error);
    });
  }, [id]);

  useEffect(() => {
    const tabKey = query.get('tab');
    setCurrentTab(tabKey !== null ? tabKey : 'house');
  }, [query]);

  useEffect(() => {
    if (statement) {
      document.title = `${statement.unit_name} - ${statement.month_display} | Statement Details`;
    }
  }, [statement]);

  const expenseColumns = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 80,
      editable: false,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      editable: false,
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      align: 'right' as const,
      width: 170,
      editable: true,
      render: (value: number) => financeAmtFormat(value),
    },
    {
      title: 'Sold Fractions Amount',
      dataIndex: 'sold_fractions_amount',
      align: 'right' as const,
      width: 200,
      editable: true,
      render: (value: number) => financeAmtFormat(value),
    },
    {
      title: 'Billable',
      dataIndex: 'billable',
      align: 'center' as const,
      width: 120,
      editable: false,
      render: (billable: boolean) => (
        <Badge status={billable ? 'success' : 'default'} text={billable ? 'Yes' : 'No'} />
      ),
    },
    {
      title: 'Actions',
      width: 100,
      render: (_: any, record: any) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Button type="link" onClick={() => save(record)} style={{ marginRight: 8 }}>
              Save
            </Button>
            <Button type="link" onClick={cancel}>
              Cancel
            </Button>
          </span>
        ) : (
          <Button type="link" disabled={editingKey !== ''} onClick={() => edit(record)}>
            Edit
          </Button>
        );
      },
    },
  ];

  const mergedColumns = expenseColumns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record: record || {},
        dataIndex: col.dataIndex || '',
        title: col.title || '',
        editing: record ? isEditing(record) : false,
        onSave: save,
      }),
    };
  });

  if (!statement) {
    return <div>Loading...</div>;
  }

  return (
    <Container className="anti-contento" isDarkTheme={isDarkTheme}>
      <div className="contento" style={{ paddingBottom: 8 }}>
        <PageHeader
          onBack={() => navigate('/statements')}
          title={statement.unit_name}
          subTitle={`Statement for ${statement.month_display}`}
          extra={statement ? [
            <RecordTable
              recordModel={InvoiceModel}
              recordsetFilters={{
                unit: statement.unit,
                billing_period: statement.month
              }}
              modalView
              allowRowSelect
              headerExtra={ExpenseStatementModel.customActions !== undefined && ExpenseStatementModel.customActions?.length > 0 && (
                <CustomActionButton customAction={ExpenseStatementModel.customActions[0]} recordModel={ExpenseStatementModel} recordId={statement.id}  />
              )}
            />,
            ExpenseStatementModel.customActions !== undefined && ExpenseStatementModel.customActions.length > 1 && (
                <CustomActionButton customAction={ExpenseStatementModel.customActions[1]} recordModel={ExpenseStatementModel} />
            ),
            <Dropdown
              disabled={updatingStatement}
              overlay={() => (
                  <Menu>
                      {statement?.is_finalized ? (
                          <Menu.Item onClick={() => changeStatus(false)}>Un-finalize</Menu.Item>
                      ) : (
                          <Menu.Item onClick={() => changeStatus(true)}>Finalize</Menu.Item>
                      )}
                  </Menu>
              )}
              key="1"
              trigger={["click"]}
          >
              <Button className="capitalize" type="default" loading={updatingStatement}>
                  <Badge status={statement?.is_finalized ? 'success' : 'processing'} /><Space>{statement?.is_finalized ? 'Finalized' : 'Draft'}
                  <DownOutlined /></Space>
              </Button>
          </Dropdown>,
          ] : []}
        />
      </div>

      <Tabs
        renderTabBar={renderTabBar}
        defaultActiveKey="house"
        activeKey={currentTab}
        className="mt-sm mb-xl"
        type="card"
        tabBarGutter={4}
        onTabClick={(key) => {
          handleFilter('tab', key, true);
          setCurrentTab(key);
        }}
        style={{ background: isDarkTheme ? '#2a2a2a' : 'white' }}
      >
        <Tabs.TabPane
          closable={false}
          className="sidento pb-xl"
          tab={<div><HomeOutlined />House Statement</div>}
          key="house"
        >
          <div>
            {/* Balance Summary */}
            <div className="balance-summary">
              <div className="balance-card">
                <div className="balance-row">
                  <span className="balance-label">Initial Balance:</span>
                  <span className={`balance-amount ${statement.expense_initial_balance >= 0 ? 'positive' : 'negative'}`}>
                    {financeAmtFormat(statement.expense_initial_balance)}
                  </span>
                </div>
                <div className="balance-row">
                  <span className="balance-label">Total Expenses:</span>
                  <span className="balance-amount">
                    {financeAmtFormat(statement.total_expenses)}
                  </span>
                </div>
                <div className="balance-row" style={{ borderTop: `1px solid ${isDarkTheme ? '#424242' : '#e8e8e8'}`, paddingTop: 8, marginTop: 8 }}>
                  <span className="balance-label">Final Balance:</span>
                  <span className={`balance-amount ${statement.expense_final_balance >= 0 ? 'positive' : 'negative'}`}>
                    {financeAmtFormat(statement.expense_final_balance)}
                  </span>
                </div>
              </div>
            </div>

            {/* Expenses Table */}
            <div className="fs-3-25 fw-600 mb-sm">Property Expenses</div>
            <Form component={false}>
              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                dataSource={expenseData}
                columns={mergedColumns}
                rowClassName="editable-row"
                pagination={false}
                size="small"
                sticky={{ offsetHeader: 32 }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <strong>Total Expenses</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        <div className="numbers-cont fw-600">
                          {financeAmtFormat(expenseData.reduce((sum, item) => sum + item.total_amount, 0))}
                        </div>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <div className="numbers-cont fw-600">
                          {financeAmtFormat(expenseData.reduce((sum, item) => sum + item.sold_fractions_amount, 0))}
                        </div>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} colSpan={2} />
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Form>

            {/* Reserve Fund Section */}
            {statement.reserve_fund_json && statement.reserve_fund_json.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <div className="fs-3-25 fw-600 mb-sm">Reserve Fund</div>
                <Table
                  dataSource={statement.reserve_fund_json.map((item: any, index: number) => ({
                    key: index,
                    ...item
                  }))}
                  columns={[
                    { title: 'Type', dataIndex: 'type', width: 200 },
                    { title: 'Description', dataIndex: 'description' },
                    { title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: (value: number) => financeAmtFormat(value) },
                    { title: 'Date', dataIndex: 'date', width: 120, render: (date: string) => moment(date).format('MMM DD, YYYY') },
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>
            )}
          </div>
        </Tabs.TabPane>

        {/* Owner Statement Tabs */}
        {ownerStatements.map((ownerStatement, index) => (
          <Tabs.TabPane
            key={`owner-${ownerStatement.id}`}
            tab={<div><UserOutlined />{ownerStatement.owner_name}</div>}
            className="sidento pb-xl"
          >
            <div>
              <div className="flex flex-row gap-md">
              {/* Owner info and fractions */}
              <div className="property-header flx1" style={{ alignContent: 'center' }}>
                <div className="property-info">
                  <div className="property-detail">
                    <span className="property-label">Owner</span>
                    <span className="property-value">{ownerStatement.owner_name}</span>
                  </div>
                  <div className="property-detail">
                    <span className="property-label">Email</span>
                    <span className="property-value">{ownerStatement.owner_email}</span>
                  </div>
                  <div className="property-detail">
                    <span className="property-label">Fractions</span>
                    <span className="property-value">
                      {ownerStatement.fraction_numbers.join(', ')} ({ownerStatement.fraction_count} total)
                    </span>
                  </div>
                </div>
              </div>

              {/* Owner Balance Summary */}
              <div className="balance-summary">
                <div className="balance-card" style={{ minWidth: 280 }}>
                  <div className="balance-row">
                    <span className="balance-label">Initial Expense Balance:</span>
                    <span className={`balance-amount ${ownerStatement.expense_initial_balance >= 0 ? 'positive' : 'negative'}`}>
                      {financeAmtFormat(ownerStatement.expense_initial_balance)}
                    </span>
                  </div>
                  <div className="balance-row">
                    <span className="balance-label">Final Expense Balance:</span>
                    <span className={`balance-amount ${ownerStatement.expense_final_balance >= 0 ? 'positive' : 'negative'}`}>
                      {financeAmtFormat(ownerStatement.expense_final_balance)}
                    </span>
                  </div>
                  <div className="balance-row" style={{ borderTop: `1px solid ${isDarkTheme ? '#424242' : '#e8e8e8'}`, paddingTop: 8, marginTop: 8 }}>
                    <span className="balance-label">STR Rental Balance:</span>
                    <span className={`balance-amount ${ownerStatement.str_rental_balance >= 0 ? 'positive' : 'negative'}`}>
                      {financeAmtFormat(ownerStatement.str_rental_balance)}
                    </span>
                  </div>
                </div>
              </div>
              </div>

              {/* Owner Expenses Table */}
              <div style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div className="fs-3-25 fw-600">Owner's Portion of Expenses</div>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => handleAddExpenseItem(ownerStatement.id)}
                  >
                    Add Line Item
                  </Button>
                </div>
                <Table
                  dataSource={(ownerStatement.expenses_json || []).map((item: any, index: number) => ({
                    key: index,
                    ...item
                  }))}
                  columns={[
                    {
                      title: 'Description',
                      dataIndex: 'description'
                    },
                    {
                      title: 'Property Amount',
                      dataIndex: 'property_total',
                      align: 'right' as const,
                      width: 140,
                      render: (value: number) => financeAmtFormat(value || 0)
                    },
                    {
                      title: 'Fraction Amount',
                      dataIndex: 'fraction_amount',
                      align: 'right' as const,
                      width: 140,
                      render: (value: number, record: any) => (
                        record.code === 'DEPOSIT' ? null : financeAmtFormat(value || 0)
                      )
                    },
                    {
                      title: 'Deposits',
                      dataIndex: 'fraction_amount',
                      align: 'right' as const,
                      width: 140,
                      render: (value: number, record: any) => (
                        record.code === 'DEPOSIT' ? financeAmtFormat(value || 0) : null
                      )
                    },
                    {
                      title: 'Running Balance',
                      dataIndex: 'running_balance',
                      align: 'right' as const,
                      width: 140,
                      render: (value: number, record: any) => (
                        record.code === 'HEADER' || record.code === 'F01' ? null : (
                          <span className={value >= 0 ? 'balance-amount positive' : 'balance-amount negative'}>
                            {financeAmtFormat(value || 0)}
                          </span>
                        )
                      )
                    }
                  ]}
                  pagination={false}
                  size="small"
                  summary={() => {
                    const filteredData = (ownerStatement.expenses_json || []).filter((item: any) => item.code !== 'HEADER');
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={1}>
                            <strong>Total Owner Expenses</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2}>
                            <div className="numbers-cont fw-600">
                              {financeAmtFormat(
                                filteredData.reduce((sum: number, item: any) =>
                                  sum + (item.property_total || 0), 0
                                )
                              )}
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3}>
                            <div className="numbers-cont fw-600">
                              {financeAmtFormat(
                                filteredData.reduce((sum: number, item: any) =>
                                  item.code !== 'DEPOSIT' ? sum + (item.fraction_amount || 0) : sum, 0
                                )
                              )}
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4}>
                            <div className="numbers-cont fw-600">
                              {financeAmtFormat(
                                filteredData.reduce((sum: number, item: any) =>
                                  item.code === 'DEPOSIT' ? sum + (item.fraction_amount || 0) : sum, 0
                                )
                              )}
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    )
                  }}
                  rowClassName={(record: any) => (record.code === 'HEADER' || record.code === 'F01') ? 'header-row' : ''}
                />
              </div>

              {/* STR Rental Transactions */}
              {ownerStatement.str_rental_json && ownerStatement.str_rental_json.length > 0 && (
                <div style={{ marginTop: 32 }}>
                  <div className="fs-3-25 fw-600 mb-sm">STR Rental Transactions</div>
                  <Table
                    dataSource={ownerStatement.str_rental_json.map((item: any, index: number) => ({
                      key: index,
                      ...item
                    }))}
                    columns={[
                      {
                        title: 'Type',
                        dataIndex: 'type',
                        width: 120,
                        render: (type: string) => (
                          <Badge
                            status={type === 'credit' ? 'success' : 'processing'}
                            text={type?.charAt(0).toUpperCase() + type?.slice(1) || 'N/A'}
                          />
                        )
                      },
                      {
                        title: 'Description',
                        dataIndex: 'description'
                      },
                      {
                        title: 'Amount',
                        dataIndex: 'amount',
                        align: 'right' as const,
                        width: 140,
                        render: (value: number) => (
                          <span className={value >= 0 ? 'balance-amount positive' : 'balance-amount negative'}>
                            {financeAmtFormat(value || 0)}
                          </span>
                        )
                      },
                      {
                        title: 'Date',
                        dataIndex: 'date',
                        width: 120,
                        render: (date: string) => date ? moment(date).format('MMM DD, YYYY') : 'N/A'
                      }
                    ]}
                    pagination={false}
                    size="small"
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            <strong>Total STR Transactions</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2}>
                            <div className="numbers-cont fw-600">
                              {financeAmtFormat(
                                ownerStatement.str_rental_json.reduce((sum: number, item: any) =>
                                  sum + (item.amount || 0), 0
                                )
                              )}
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </div>
              )}

              {/* Additional Balance Details */}
              {ownerStatement.balances_json && Object.keys(ownerStatement.balances_json).length > 0 && (
                <div style={{ marginTop: 32 }}>
                  <div className="fs-3-25 fw-600 mb-sm">Balance Details</div>
                  <div style={{ background: isDarkTheme ? '#333333' : '#fafafa', padding: '16px', borderRadius: '4px' }}>
                    {Object.entries(ownerStatement.balances_json).map(([key, value]: [string, any]) => {
                      if (typeof value === 'object' && value !== null) {
                        return (
                          <div key={key} style={{ marginBottom: '16px' }}>
                            <div style={{ fontWeight: 600, marginBottom: '8px', textTransform: 'capitalize' }}>
                              {key.replace(/_/g, ' ')}:
                            </div>
                            {Object.entries(value).map(([subKey, subValue]: [string, any]) => (
                              <div key={subKey} className="balance-row" style={{ fontSize: '14px' }}>
                                <span className="balance-label" style={{ textTransform: 'capitalize' }}>
                                  {subKey.replace(/_/g, ' ')}:
                                </span>
                                <span className={`balance-amount ${Number(subValue) >= 0 ? 'positive' : 'negative'}`}>
                                  {financeAmtFormat(Number(subValue) || 0)}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <div key={key} className="balance-row">
                          <span className="balance-label" style={{ textTransform: 'capitalize' }}>
                            {key.replace(/_/g, ' ')}:
                          </span>
                          <span className={`balance-amount ${Number(value) >= 0 ? 'positive' : 'negative'}`}>
                            {financeAmtFormat(Number(value) || 0)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Tabs.TabPane>
        ))}
      </Tabs>

      {/* Add Expense Item Modal */}
      <Modal
        title="Add New Expense Line Item"
        visible={addExpenseModalVisible}
        onOk={handleAddExpenseSubmit}
        onCancel={() => setAddExpenseModalVisible(false)}
        okText="Add Expense"
        cancelText="Cancel"
        width={600}
      >
        <Form
          form={addExpenseForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter expense description' }]}
          >
            <Input placeholder="Enter expense description" />
          </Form.Item>

          <Form.Item
            name="property_total"
            label="Property Total"
            rules={[{ required: true, message: 'Please enter property total amount' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              formatter={(value) => `${value || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseFloat(value?.replace(/\$\s?|(,*)/g, '') || '0')}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name="fraction_amount"
            label="Fraction Amount"
            rules={[{ required: true, message: 'Please enter fraction amount' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              formatter={(value) => `${value || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseFloat(value?.replace(/\$\s?|(,*)/g, '') || '0')}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name="running_balance"
            label="Running Balance"
            rules={[{ required: false, message: 'Please enter running balance' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              formatter={(value) => `${value || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseFloat(value?.replace(/\$\s?|(,*)/g, '') || '0')}
              step={0.01}
              precision={2}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
}

export default StatementDetail;
